import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Megaphone, Mail, FileText, Newspaper, MessageSquare, Settings as SettingsIcon, KeyRound, Users, HelpCircle, AlertCircle, ShoppingBag, Vote, Map as MapIcon, History } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ADMIN_PATH } from "@/App";

import KomunikatyAdmin from "./admin/KomunikatyAdmin";
import WiadomosciAdmin from "./admin/WiadomosciAdmin";
import UchwalyAdmin from "./admin/UchwalyAdmin";
import NewsletterAdmin from "./admin/NewsletterAdmin";
import KontaktAdmin from "./admin/KontaktAdmin";
import ZgloszeniaAdmin from "./admin/ZgloszeniaAdmin";
import OgloszeniaAdmin from "./admin/OgloszeniaAdmin";
import PollsAdmin from "./admin/PollsAdmin";
import FaqAdmin from "./admin/FaqAdmin";
import UstawieniaAdmin from "./admin/UstawieniaAdmin";
import KontoAdmin from "./admin/KontoAdmin";
import UzytkownicyAdmin from "./admin/UzytkownicyAdmin";
import MapaAdmin from "./admin/MapaAdmin";
import AuditAdmin from "./admin/AuditAdmin";

const LOGO_URL = "/logo.png";

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loadingMe, setLoadingMe] = useState(true);
    const [unreadMsg, setUnreadMsg] = useState(0);
    const [unreadReports, setUnreadReports] = useState(0);
    const [pendingListings, setPendingListings] = useState(0);

    useEffect(() => {
        document.title = "Panel administratora – Dąbrówka Wiślana";
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => navigate(ADMIN_PATH))
            .finally(() => setLoadingMe(false));
    }, [navigate]);

    const refreshCounts = () => {
        api.get("/contact/messages/unread-count").then((r) => setUnreadMsg(r.data?.count || 0)).catch(() => {});
        api.get("/reports/unread-count").then((r) => setUnreadReports(r.data?.count || 0)).catch(() => {});
        api.get("/listings/admin/unread-count").then((r) => setPendingListings(r.data?.count || 0)).catch(() => {});
    };

    useEffect(() => {
        if (!user) return;
        refreshCounts();
        const t = setInterval(refreshCounts, 30000);
        return () => clearInterval(t);
    }, [user]);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        navigate(ADMIN_PATH);
    };

    if (loadingMe) {
        return <div className="min-h-screen flex items-center justify-center text-gray-500">Wczytywanie...</div>;
    }

    const can = (perm) => user?.role === "admin" || !!(user?.permissions?.[perm]);
    // Workers may only have `handle_assigned_reports` → still show Zgłoszenia tab.
    const canSeeReports = can("manage_reports") || can("handle_assigned_reports");
    const defaultTab = can("manage_announcements") ? "komunikaty" : can("manage_news") ? "wiadomosci" : canSeeReports ? "zgloszenia" : "konto";

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F3]">
            <header className="bg-[#092E22] text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full ring-2 ring-[#D4A537]/40" />
                        <div>
                            <p className="font-serif text-lg">Dąbrówka Wiślana</p>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4A537]">Panel administratora</p>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-white/70 hidden sm:inline">{user?.email}</span>
                        <Button onClick={logout} variant="outline" data-testid="admin-logout-button" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10">
                            <LogOut size={14} className="mr-2" /> Wyloguj
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                <Tabs defaultValue={defaultTab} className="space-y-6">
                    <TabsList className="bg-white rounded-full p-1.5 h-auto flex flex-wrap" data-testid="admin-tabs">
                        {can("manage_announcements") && (
                            <TabsTrigger value="komunikaty" data-testid="tab-komunikaty" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <Megaphone size={14} className="mr-2" /> Komunikaty
                            </TabsTrigger>
                        )}
                        {can("manage_news") && (
                            <TabsTrigger value="wiadomosci" data-testid="tab-wiadomosci" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <Newspaper size={14} className="mr-2" /> Wiadomości
                            </TabsTrigger>
                        )}
                        {can("manage_resolutions") && (
                            <TabsTrigger value="uchwaly" data-testid="tab-uchwaly" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <FileText size={14} className="mr-2" /> Uchwały
                            </TabsTrigger>
                        )}
                        {can("manage_newsletter") && (
                            <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <Mail size={14} className="mr-2" /> Mailing
                            </TabsTrigger>
                        )}
                        {can("manage_contact") && (
                            <TabsTrigger value="kontakt" data-testid="tab-kontakt" className="relative rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <MessageSquare size={14} className="mr-2" /> Wiadomości kontakt.
                                {unreadMsg > 0 && (
                                    <span data-testid="tab-kontakt-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                                        {unreadMsg}
                                    </span>
                                )}
                            </TabsTrigger>
                        )}
                        {canSeeReports && (
                            <TabsTrigger value="zgloszenia" data-testid="tab-zgloszenia" className="relative rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <AlertCircle size={14} className="mr-2" /> Zgłoszenia
                                {unreadReports > 0 && (
                                    <span data-testid="tab-zgloszenia-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                                        {unreadReports}
                                    </span>
                                )}
                            </TabsTrigger>
                        )}
                        {can("manage_listings") && (
                            <TabsTrigger value="ogloszenia" data-testid="tab-ogloszenia" className="relative rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <ShoppingBag size={14} className="mr-2" /> Ogłoszenia
                                {pendingListings > 0 && (
                                    <span data-testid="tab-ogloszenia-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                                        {pendingListings}
                                    </span>
                                )}
                            </TabsTrigger>
                        )}
                        {can("manage_polls") && (
                            <TabsTrigger value="sondy" data-testid="tab-sondy" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <Vote size={14} className="mr-2" /> Sondy
                            </TabsTrigger>
                        )}
                        {can("manage_map") && (
                            <TabsTrigger value="mapa" data-testid="tab-mapa" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <MapIcon size={14} className="mr-2" /> Mapa
                            </TabsTrigger>
                        )}
                        {can("view_audit") && (
                            <TabsTrigger value="audit" data-testid="tab-audit" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <History size={14} className="mr-2" /> Historia
                            </TabsTrigger>
                        )}
                        {can("manage_faqs") && (
                            <TabsTrigger value="faq" data-testid="tab-faq" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <HelpCircle size={14} className="mr-2" /> FAQ
                            </TabsTrigger>
                        )}
                        {can("manage_settings") && (
                            <TabsTrigger value="ustawienia" data-testid="tab-ustawienia" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <SettingsIcon size={14} className="mr-2" /> Ustawienia
                            </TabsTrigger>
                        )}
                        {can("manage_users") && (
                            <TabsTrigger value="uzytkownicy" data-testid="tab-uzytkownicy" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                                <Users size={14} className="mr-2" /> Użytkownicy
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="konto" data-testid="tab-konto" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0D4F3C] data-[state=active]:text-white">
                            <KeyRound size={14} className="mr-2" /> Moje konto
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="komunikaty"><KomunikatyAdmin /></TabsContent>
                    <TabsContent value="wiadomosci"><WiadomosciAdmin /></TabsContent>
                    <TabsContent value="uchwaly"><UchwalyAdmin /></TabsContent>
                    <TabsContent value="newsletter"><NewsletterAdmin /></TabsContent>
                    <TabsContent value="kontakt"><KontaktAdmin onUpdate={refreshCounts} /></TabsContent>
                    <TabsContent value="zgloszenia"><ZgloszeniaAdmin onUpdate={refreshCounts} /></TabsContent>
                    <TabsContent value="ogloszenia"><OgloszeniaAdmin onUpdate={refreshCounts} /></TabsContent>
                    <TabsContent value="sondy"><PollsAdmin /></TabsContent>
                    <TabsContent value="mapa"><MapaAdmin /></TabsContent>
                    <TabsContent value="audit"><AuditAdmin /></TabsContent>
                    <TabsContent value="faq"><FaqAdmin /></TabsContent>
                    <TabsContent value="ustawienia"><UstawieniaAdmin /></TabsContent>
                    <TabsContent value="uzytkownicy"><UzytkownicyAdmin currentUser={user} /></TabsContent>
                    <TabsContent value="konto"><KontoAdmin user={user} onUserUpdate={setUser} /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
